import { useLayoutEffect, useState } from 'react';

/** How long a load may take before it is announced. A quick one shows an
 *  empty page and then the page; only a slow one says it is loading. */
export const LOADING_DELAY_MS = 1000;

/** A loading line replaced by another — one boot step handing over to the
 *  next — keeps showing instead of blanking for another delay. */
const HANDOVER_MS = 200;

let lastOnScreen = Number.NEGATIVE_INFINITY;

/** False until the load has taken [delay], then true. */
export function useLoadingShown(delay: number = LOADING_DELAY_MS): boolean {
  const [shown, setShown] = useState(false);
  // Layout effects, so a successor mounting in the same commit sees its
  // predecessor's cleanup and takes over before anything is painted.
  useLayoutEffect(() => {
    if (shown) {
      return () => {
        lastOnScreen = Date.now();
      };
    }
    if (Date.now() - lastOnScreen < HANDOVER_MS) {
      setShown(true);
      return;
    }
    const timer = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(timer);
  }, [shown, delay]);
  return shown;
}

/**
 * What a page shows while it cannot show itself: nothing at first, then one
 * quiet line. A long cold read can also say how far it has got, as a bar —
 * never as a count.
 */
export function Loading({
  title,
  detail,
  progress,
}: {
  title: string;
  /** One line under it, where the wait has a reason worth naming. */
  detail?: string;
  /** From 0 to 1, when the load knows. */
  progress?: number | null;
}) {
  const shown = useLoadingShown();
  return (
    <section className="loading" aria-busy="true">
      {shown && (
        <>
          <p className="loading-title" role="status">
            {title}
          </p>
          {detail && <p className="loading-detail">{detail}</p>}
          {progress != null && (
            <div className="loading-bar" aria-hidden="true">
              <span style={{ width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%` }} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
